import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './MyProfilePage.css';
import { API_BASE_URL } from '../api/config';


function MyProfilePage({ username, onBack, isViewOnly = false }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!username) {
      setError('사용자 정보가 없습니다.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/users/${encodeURIComponent(username)}`
      );
      setProfile(res.data);
      setLocation(res.data.location || '');
      setBio(res.data.bio || '');
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 404
          ? '사용자를 찾을 수 없습니다.'
          : '프로필을 불러올 수 없습니다.');
      setError(msg);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/users/${encodeURIComponent(username)}`,
        { location, bio }
      );
      setProfile(res.data);
      setEditing(false);
      alert('프로필이 업데이트되었습니다.');
    } catch (err) {
      alert('업데이트 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const recentPosts = Array.isArray(profile?.recentPosts) ? profile.recentPosts : [];

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <img src="/images/logoTextWhite.png" alt="공구나라" className="app-sidebar-logo" />
      </aside>

      <main className="app-main">
        <div className="app-card profile-card">
          <div className="app-card-header">
            <button type="button" className="app-back" onClick={onBack}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>뒤로</span>
            </button>
            <h2 className="app-card-title">
              {isViewOnly ? '프로필' : '내 프로필'}
            </h2>
          </div>

          <div className="app-card-body">
            {loading && <div className="app-empty">불러오는 중…</div>}
            {!loading && error && <div className="app-error">{error}</div>}

            {!loading && !error && profile && (
              <>
                <div className="profile-hero">
                  <div className="profile-avatar">
                    {(profile.nickname || profile.username || '?').slice(0, 1)}
                  </div>
                  <div>
                    <div className="profile-name">{profile.nickname || profile.username}</div>
                    <div className="profile-id">@{profile.username}</div>
                  </div>
                </div>

                <div className="profile-stats">
                  <div className="profile-stat">
                    <strong>{profile.postCount ?? recentPosts.length}</strong>
                    <span>게시물</span>
                  </div>
                </div>

                <div className="profile-fields">
                  <div className="profile-field">
                    <label className="app-label">거주지역</label>
                    {editing ? (
                      <input
                        className="app-input"
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="예: 서울 강남"
                      />
                    ) : (
                      <div className="profile-value">{profile.location || '미설정'}</div>
                    )}
                  </div>

                  <div className="profile-field">
                    <label className="app-label">자기소개</label>
                    {editing ? (
                      <textarea
                        className="app-textarea"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        placeholder="간단한 소개를 적어보세요"
                      />
                    ) : (
                      <div className="profile-value">{profile.bio || '미설정'}</div>
                    )}
                  </div>
                </div>

                <div className="profile-actions">
                  {!isViewOnly && (
                    editing ? (
                      <>
                        <button
                          type="button"
                          className="app-btn"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? '저장 중…' : '저장'}
                        </button>
                        <button
                          type="button"
                          className="app-btn secondary"
                          onClick={() => {
                            setEditing(false);
                            setLocation(profile.location || '');
                            setBio(profile.bio || '');
                          }}
                        >
                          취소
                        </button>
                      </>
                    ) : (
                      <button type="button" className="app-btn" onClick={() => setEditing(true)}>
                        프로필 수정
                      </button>
                    )
                  )}
                </div>

                <section className="profile-posts">
                  <h3 className="profile-posts-title">최근 게시물</h3>
                  {recentPosts.length === 0 ? (
                    <div className="app-empty">작성한 게시물이 없습니다.</div>
                  ) : (
                    <ul className="profile-post-list">
                      {recentPosts.map((post) => (
                        <li key={post.id} className="profile-post-item">
                          {post.attachment ? (
                            <img src={post.attachment} alt="" className="profile-post-thumb" />
                          ) : (
                            <div className="profile-post-thumb placeholder">공구</div>
                          )}
                          <div className="profile-post-info">
                            <div className="profile-post-name">{post.title}</div>
                            <div className="profile-post-meta">
                              {Number(post.priceCents || 0).toLocaleString()}원
                              {post.headcount != null &&
                                ` · ${post.currentParticipants || 0}/${post.headcount}명`}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default MyProfilePage;
